import React, {useCallback, useMemo} from 'react';
import Box from '@mui/material/Box';
import {useParams} from 'react-router';
import {Concept} from 'types';
import type {Data} from 'schemaDefinition/types';
import {useLocalization} from 'localization';
import {useGetWorkQuery} from 'services/metadata';
import {FlexBox, Scene, Text, useSnacks} from 'components';
import {RtkContent} from 'components/rtkContent';
import {useSchema} from 'schemas';
import {CompareEntities} from 'widgets/compareEntities';
import {useAgentFormConfiguration} from 'editors/agent/hooks/useAgentFormConfiguration';
import {useSceneExtensionsContext} from 'sceneExtensions';
import {ExpressionPreview} from '../../feature/linkedValueSearch/components/ExpressionPreview';
import {DataFormCompareLayout} from '../../schema/form/components/DataFormCompareLayout';

export const MergeWorkSceneCore: React.FC = () => {
  const {t} = useLocalization();
  const {errorSnack} = useSnacks();
  const {currentExtension} = useSceneExtensionsContext();
  const configuration = useAgentFormConfiguration();

  const {targetId: targetIdParam, sourceId: sourceIdParam} = useParams();
  const targetId = targetIdParam || '';
  const sourceId = sourceIdParam || '';

  const {
    data: targetData,
    status: targetStatus,
    error: targetError,
  } = useGetWorkQuery({
    id: targetId,
  });
  const {
    data: sourceData,
    status: sourceStatus,
    error: sourceError,
  } = useGetWorkQuery({
    id: sourceId,
  });

  const schema = useSchema(Concept.work);

  const onSave = useCallback(
    (_target: Data, _source: Data) => {
      errorSnack('Ikke aktivert/implementert ennå');
      return Promise.reject();
    },
    [errorSnack],
  );

  const {targetExpressions, targetManifestations} = useMemo(() => {
    return {
      targetExpressions: targetData?.expressions ?? [],
      targetManifestations: targetData?.manifestations ?? [],
    };
  }, [targetData?.expressions, targetData?.manifestations]);

  const {sourceExpressions, sourceManifestations} = useMemo(() => {
    return {
      sourceExpressions: sourceData?.expressions ?? [],
      sourceManifestations: sourceData?.manifestations ?? [],
    };
  }, [sourceData?.expressions, sourceData?.manifestations]);

  return (
    <Scene
      browserTitle={t('appbar.mergeWork')}
      rightPanelConfig={currentExtension?.panelConfig}>
      <Scene.Header title={t('appbar.mergeWork')} />
      <Scene.Content>
        <RtkContent status={targetStatus} error={targetError}>
          <RtkContent status={sourceStatus} error={sourceError}>
            {targetData && sourceData ? (
              <>
                <CompareEntities
                  enableCollapseMainEntity
                  compareEntitiesContextProps={{
                    targetData: targetData.work,
                    sourceData: sourceData.work,
                    schema,
                    onSave,
                  }}
                  compareEntitiesFormProps={{
                    formConfiguration: configuration,
                    targetHeader: (
                      <Box ml={2}>
                        <Text variant="h4" textTransform="uppercase">
                          Verk som beholdes
                        </Text>
                        <Text variant="caption">
                          Dette verket vil få alle uttrykk og koblinger, og
                          eventuelle endringer som gjøres.
                        </Text>
                      </Box>
                    ),
                    sourceHeader: (
                      <Box ml={2}>
                        <Text variant="h4" textTransform="uppercase">
                          Verk som slettes
                        </Text>
                        <Text variant="caption">
                          Uttrykk og koblinger vil bli flyttet fra dette verket
                          til det andre verket, og dette verket vil deretter bli
                          slettet.
                        </Text>
                      </Box>
                    ),
                  }}
                />
              </>
            ) : null}
          </RtkContent>
        </RtkContent>

        {targetExpressions && sourceExpressions ? (
          <FlexBox horizontal flex={1} mb={4}>
            <DataFormCompareLayout
              renderCurrent={() => (
                <>
                  {targetExpressions.map(e => (
                    <Box key={e.id} mb={2}>
                      <ExpressionPreview
                        expression={e}
                        manifestations={targetManifestations.filter(
                          m => m.expressionId === e.id,
                        )}
                        configuration={{
                          layout: {},
                          relatedScope: targetData,
                        }}
                      />
                    </Box>
                  ))}
                </>
              )}
              renderProposed={() => (
                <>
                  {sourceExpressions.map(e => (
                    <Box key={e.id} mb={2}>
                      <ExpressionPreview
                        expression={e}
                        manifestations={sourceManifestations.filter(
                          m => m.expressionId === e.id,
                        )}
                        configuration={{
                          layout: {},
                          relatedScope: sourceData,
                        }}
                      />
                    </Box>
                  ))}
                </>
              )}
            />
          </FlexBox>
        ) : null}
      </Scene.Content>
      {currentExtension ? (
        <Scene.RightPanel>
          {currentExtension.renderRightPanel()}
        </Scene.RightPanel>
      ) : null}
    </Scene>
  );
};
